import { useAccess } from "./AccessContext";

// Which warehouses may this person pick in a warehouse filter or selector?
//
// The `cross_warehouse` matrix row ("Other warehouses", D14/D15) is the one rule: with N the person
// works in their own warehouse only, so every warehouse dropdown shows only that warehouse (or a
// static label) and no "All" / group entries. R or W shows every warehouse. Admin holds every key.
// The BE enforces the same rule (403 warehouse_scope); this keeps the UI from offering choices the
// server will refuse (2026-09-30, staging finding on Orders).
//
// Before the access map settles (or outside a provider) nothing is restricted, so no page loses a
// dropdown on its first frame and isolated renders/tests behave as before.
export interface WarehouseScope {
    /** May pick another warehouse (cross_warehouse read or write). */
    canCross: boolean;
    /** The caller's own warehouse (session user), "" when unknown. */
    own: string;
    /** Narrow a list of warehouse names to what may be offered. */
    limit: (warehouses: string[]) => string[];
}

const readOwnWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        const u = raw ? JSON.parse(raw) : null;
        return typeof u?.warehouse === "string" ? u.warehouse : "";
    } catch {
        return "";
    }
};

export const useWarehouseScope = (): WarehouseScope => {
    const { authoritative, hasAccess } = useAccess();
    const own = readOwnWarehouse();
    const canCross = !authoritative || hasAccess("cross_warehouse");
    return {
        canCross,
        own,
        limit: (warehouses) => (canCross ? warehouses : warehouses.filter(w => w === own)),
    };
};
